import type {
	AnsweredQuestion,
	AnyQuestion,
	ProductAmlQuestionSet,
	SniCode,
} from "@sveabank/cc-common";
import { onboardingApi } from ".";
import type { BeneficialOwnerDetails } from "../types/beneficial-ownership";
import { applicationsApiSlice } from "./application";
import {
	assignSniCodes,
	confirmBeneficialOwners,
	fetchAmlQuestions,
	fetchBeneficialOwner,
	fetchQuestions,
	fetchSniCodes,
	postAmlQuestions,
	postApplicationQuestions,
	postBeneficialOwnerQuestions,
} from "./endpoints/kyc";

const kycApiSlice = onboardingApi.injectEndpoints({
	endpoints: (builder) => ({
		fetchAmlQuestions: builder.query<ProductAmlQuestionSet[], string>({
			query: (applicationId) => fetchAmlQuestions(applicationId),
			transformResponse: (response: { data: ProductAmlQuestionSet[] }) => response.data,
			providesTags: ["AMLQuestions"],
			extraOptions: { withAuth: true },
		}),

		fetchQuestions: builder.query<
			AnyQuestion[],
			{ applicationId: string; questionIds: Array<string> }
		>({
			query: ({ applicationId, questionIds }) => fetchQuestions(applicationId, questionIds),
			extraOptions: { withAuth: true },
			transformResponse: (response: { data: Array<AnyQuestion> }) => response.data,
		}),

		postApplicationQuestions: builder.mutation<
			null,
			{
				questions: Array<AnsweredQuestion>;
				applicationId: string;
			}
		>({
			async queryFn({ applicationId, questions }, queryApi, _extraOptions, fetchWithBaseQuery) {
				try {
					await fetchWithBaseQuery(postApplicationQuestions(applicationId, questions));

					await queryApi
						.dispatch(
							applicationsApiSlice.endpoints.fetchSteps.initiate(applicationId, {
								forceRefetch: true,
							}),
						)
						.unwrap();

					return { data: null };
				} catch (error) {
					return {
						error: {
							error: error as string,
							status: "CUSTOM_ERROR",
						},
					};
				}
			},
			extraOptions: {
				revision: true,
				allowRevisionConflict: false,
				withAuth: true,
			},
		}),

		postAmlQuestions: builder.mutation<
			null,
			{
				questions: Array<AnsweredQuestion>;
				applicationId: string;
				productId: string;
			}
		>({
			async queryFn(
				{ applicationId, productId, questions },
				queryApi,
				_extraOptions,
				fetchWithBaseQuery,
			) {
				try {
					await fetchWithBaseQuery(postAmlQuestions(applicationId, productId, questions));

					await queryApi
						.dispatch(
							applicationsApiSlice.endpoints.fetchSteps.initiate(applicationId, {
								forceRefetch: true,
							}),
						)
						.unwrap();

					return { data: null };
				} catch (error) {
					return {
						error: {
							error: error as string,
							status: "CUSTOM_ERROR",
						},
					};
				}
			},
			extraOptions: {
				revision: true,
				allowRevisionConflict: false,
				withAuth: true,
			},
			invalidatesTags: ["AMLQuestions"],
		}),

		fetchBeneficialOwners: builder.query<BeneficialOwnerDetails, string>({
			query: (applicationId) => fetchBeneficialOwner(applicationId),
			providesTags: ["BeneficialOwners"],
			extraOptions: { withAuth: true },
		}),

		confirmBeneficialOwners: builder.mutation<
			null,
			{
				applicationId: string;
				beneficialOwnersConfirmed: boolean;
			}
		>({
			async queryFn(
				{ applicationId, beneficialOwnersConfirmed },
				queryApi,
				_extraOptions,
				fetchWithBaseQuery,
			) {
				try {
					await fetchWithBaseQuery(
						confirmBeneficialOwners(applicationId, beneficialOwnersConfirmed),
					);

					await queryApi
						.dispatch(
							applicationsApiSlice.endpoints.fetchSteps.initiate(applicationId, {
								forceRefetch: true,
							}),
						)
						.unwrap();

					await queryApi
						.dispatch(
							kycApiSlice.endpoints.fetchBeneficialOwners.initiate(applicationId, {
								forceRefetch: true,
							}),
						)
						.unwrap();

					return { data: null };
				} catch (error) {
					return {
						error: {
							error: error as string,
							status: "CUSTOM_ERROR",
						},
					};
				}
			},
			extraOptions: {
				revision: true,
				allowRevisionConflict: false,
				withAuth: true,
			},
			invalidatesTags: ["CurrentApplication", "DetailedApplications"],
		}),

		postBeneficialOwnerQuestions: builder.mutation<
			void,
			{
				questions: Array<AnsweredQuestion>;
				applicationId: string;
				beneficialOwnerQuestionKey: string;
			}
		>({
			query: ({ applicationId, questions, beneficialOwnerQuestionKey }) =>
				postBeneficialOwnerQuestions(applicationId, questions, beneficialOwnerQuestionKey),
			invalidatesTags: ["CurrentApplication"],
			extraOptions: {
				revision: true,
				allowRevisionConflict: false,
				withAuth: true,
			},
		}),

		fetchSniCodes: builder.query<Array<SniCode>, void>({
			query: () => fetchSniCodes(),
			extraOptions: { withAuth: true },
			transformResponse: (response: { data: Array<SniCode> }) => response.data,
		}),

		assignSniCodes: builder.mutation<
			null,
			{
				applicationId: string;
				sniCodes: Array<string>;
			}
		>({
			async queryFn({ applicationId, sniCodes }, queryApi, _extraOptions, fetchWithBaseQuery) {
				try {
					await fetchWithBaseQuery(assignSniCodes(applicationId, sniCodes));

					await queryApi
						.dispatch(
							applicationsApiSlice.endpoints.fetchSteps.initiate(applicationId, {
								forceRefetch: true,
							}),
						)
						.unwrap();

					return { data: null };
				} catch (error) {
					return {
						error: {
							error: error as string,
							status: "CUSTOM_ERROR",
						},
					};
				}
			},
			extraOptions: {
				revision: true,
				allowRevisionConflict: true,
				withAuth: true,
			},
		}),
	}),
	overrideExisting: false,
});

export const {
	useFetchAmlQuestionsQuery,
	usePostApplicationQuestionsMutation,
	usePostAmlQuestionsMutation,
	useFetchBeneficialOwnersQuery,
	useConfirmBeneficialOwnersMutation,
	useFetchQuestionsQuery,
	usePostBeneficialOwnerQuestionsMutation,
	useFetchSniCodesQuery,
	useAssignSniCodesMutation,
} = kycApiSlice;
